import React from 'react';
import { Box, Typography, Link, IconButton, Button } from '@mui/material';
import { Facebook, Instagram, Twitter } from '@mui/icons-material';
import { BusinessConfig } from '../../types';

interface ContactProps {
  businessConfig: BusinessConfig;
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove anything that is not a digit
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  let coreDigits: string | null = null;

  // Supports +1XXXXXXXXXX, 1XXXXXXXXXX, or bare 10-digit numbers
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    coreDigits = digitsOnly.slice(1);
  } else if (digitsOnly.length === 10) {
    coreDigits = digitsOnly;
  }

  if (coreDigits) {
    return `+1 (${coreDigits.slice(0, 3)}) ${coreDigits.slice(3, 6)}-${coreDigits.slice(6)}`;
  }

  // Return original if we cannot safely format it
  return phoneNumber;
};


const Contact = ({ businessConfig }: ContactProps) => {
  const address = businessConfig.businessInfo.address;
  const encodedAddress = encodeURIComponent(address);
  const businessEmail = businessConfig.businessInfo.email?.trim();

  const socialLinks = [
    { href: businessConfig.socials.facebook?.trim(), Icon: Facebook, label: 'Facebook' },
    { href: businessConfig.socials.instagram?.trim(), Icon: Instagram, label: 'Instagram' },
    { href: businessConfig.socials.twitter?.trim(), Icon: Twitter, label: 'Twitter' },
  ].filter(({ href }) => !!href);

  return (
    <Box
      sx={(theme) => ({
        padding: 4,
        textAlign: 'center',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      })}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        VISIT US:
      </Typography>

      <Typography variant="body1" gutterBottom>
        {businessConfig.businessInfo.address}
      </Typography>

      <Box sx={{ my: 3 }}>
        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`}
          target="_blank"
          rel="noopener"
          sx={{ textDecoration: 'none' }}
        >
          <Button
            variant="outlined"
            sx={(theme) => ({
              width: 200,
              color: theme.palette.primary.contrastText,
              borderRadius: 10,
              paddingX: 1,
              borderColor: theme.palette.primary.contrastText,
              '&:hover': {
                borderColor: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.dark,
              },
            })}
          >
            Open in Maps
          </Button>
        </Link>
      </Box>

      <Box
        component="iframe"
        src={businessConfig.homepage.googleMapsSrc}
        height="250px"
        loading="lazy"
        style={{ border: 0 }}
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        sx={{
          width: { xs: '100%', sm: '100%', md: '50%' }, // Responsive width
          margin: '0 auto', // Center horizontally
        }}
      />

      {/* Social Media Section */}
      {socialLinks.length > 0 && (
        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            FOLLOW US:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            {socialLinks.map(({ href, Icon, label }) => (
              <Link key={label} href={href} target="_blank" rel="noopener">
                <IconButton
                  aria-label={label}
                  sx={(theme) => ({ color: theme.palette.primary.contrastText, fontSize: 40 })}
                >
                  <Icon />
                </IconButton>
              </Link>
            ))}
          </Box>
        </Box>
      )}

      {/* Click to call */}
      {businessConfig.businessInfo.phone && <Typography variant="body1" gutterBottom>
        Call us at:
        <Link
          href={"tel:" + businessConfig.businessInfo.phone}
          sx={(theme) => ({
            ml: 1,
            display: 'inline',
            color: theme.palette.primary.contrastText,
            textDecoration: 'underline',
          })}
        >
          {formatPhoneNumber(businessConfig.businessInfo.phone)}
        </Link>
      </Typography>}

      {/* Email link */}
      {businessEmail && <Typography variant="body1">
        Email us at:
        <Link
          href={`mailto:${businessEmail}`}
          sx={(theme) => ({
            ml: 1,
            display: 'inline',
            color: theme.palette.primary.contrastText,
            textDecoration: 'underline',
          })}
        >
          {businessEmail}
        </Link>
      </Typography>}
    </Box>
  );
};

export default Contact;
